import { API_BASE_URL, API_ENDPOINTS } from '../config/api';
import { api } from '../utils/apiClient';
import type { CreateProfileRequest, CreateProfileResponse } from '../types/profile';

/**
 * Create user profile
 * Requires authentication (token automatically included)
 */
export const createProfile = async (data: CreateProfileRequest): Promise<CreateProfileResponse> => {
  return api.post<CreateProfileResponse>(
    `${API_BASE_URL.PROFILE}${API_ENDPOINTS.PROFILE.CREATE}`,
    data
  );
};

/**
 * Get user profile
 * Requires authentication (token automatically included)
 */
export const getProfile = async (): Promise<CreateProfileResponse> => {
  return api.get<CreateProfileResponse>(
    `${API_BASE_URL.PROFILE}${API_ENDPOINTS.PROFILE.GET}`
  );
};

/**
 * Update user profile
 * Requires authentication (token automatically included)
 */
export const updateProfile = async (id: string, data: Partial<CreateProfileRequest>): Promise<CreateProfileResponse> => {
  console.log('Profile Service - Sending data:', data);
  console.log('Profile Service - URL:', `${API_BASE_URL.PROFILE}${API_ENDPOINTS.PROFILE.UPDATE}/${id}`);
  return api.put<CreateProfileResponse>(
    `${API_BASE_URL.PROFILE}${API_ENDPOINTS.PROFILE.UPDATE}/${id}`,
    data
  );
};

/**
 * Get presigned URL for profile image upload
 * Requires authentication (token automatically included)
 */
export const getProfileImageUploadUrl = async (contentType: string, folderType: string = 'profile-images'): Promise<{
  upload_url: string;
  image_key: string;
}> => {
  console.log('Getting presigned URL for content type:', contentType, 'folder:', folderType);
  return api.post<{ upload_url: string; image_key: string }>(
    `${API_BASE_URL.CAMPAIGN}${API_ENDPOINTS.PROFILE.UPLOAD_URL}`,
    {
      content_type: contentType,
      folder_type: folderType
    }
  );
};

/**
 * Upload image to S3 using presigned URL
 * Note: Do not add extra headers unless they were included in the presigned URL signature
 */
export const uploadImageToS3 = async (presignedUrl: string, file: File): Promise<void> => {
  console.log('Uploading image to S3:', file.name);
  console.log('File type:', file.type);
  console.log('File size:', file.size, 'bytes');

  // Upload without custom headers to avoid CORS preflight
  // The Content-Type should be handled by the presigned URL or inferred by S3
  const response = await fetch(presignedUrl, {
    method: 'PUT',
    body: file,
  });

  if (!response.ok) {
    const errorText = await response.text().catch(() => 'Unknown error');
    console.error('S3 upload failed:', response.status, errorText);
    throw new Error(`Failed to upload image to S3: ${response.status} ${response.statusText}`);
  }

  console.log('✅ Image uploaded to S3 successfully');
};

/**
 * Update profile with image key
 * Requires authentication (token automatically included)
 */
export const updateProfileImage = async (imageKey: string, imageType: string = 'profile'): Promise<{
  success: boolean;
  message: string;
  image_url?: string;
}> => {
  console.log('Updating profile with image key:', imageKey, 'type:', imageType);
  return api.post<{ success: boolean; message: string; image_url?: string }>(
    `${API_BASE_URL.CAMPAIGN}${API_ENDPOINTS.PROFILE.UPDATE_IMAGE}`,
    {
      image_key: imageKey,
      image_type: imageType
    }
  );
};

/**
 * Get profile and business images
 * Requires authentication (token automatically included)
 */
export const getProfileImages = async (): Promise<{
  success: boolean;
  data: {
    profile_image_url?: string;
    business_logo_url?: string;
  }
}> => {
  console.log('Fetching profile images from backend...');
  // Add timestamp to prevent caching
  return api.get<{
    success: boolean;
    data: {
      profile_image_url?: string;
      business_logo_url?: string;
    }
  }>(`${API_BASE_URL.CAMPAIGN}${API_ENDPOINTS.PROFILE.UPDATE_IMAGE}?t=${Date.now()}`);
};

